import { IMPORT_KINDS, type ImportKind } from '@beta-assur/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Field, PageHeader } from '../components/page';
import { ActionError, Empty, ErrorState, Forbidden, Loading } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox, Input, Select } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { instant } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useCursorList } from '../lib/infinite';
import { useApiMutation } from '../lib/query';
import { useTenant } from '../lib/referentials';
import type { ImportBatch, ImportMappingResponse, ImportUpload } from '../lib/types';

// Imports (BA-013, spec 04) : téléversement, correspondance des colonnes, vérification des
// 20 premières lignes, puis simulation et import réel sur la fiche de l'import.

const STATUS_TONE = {
  uploaded: 'neutral',
  mapping: 'info',
  validating: 'info',
  processing: 'info',
  completed: 'success',
  failed: 'danger',
} as const;

export function ImportStatusBadge({ batch }: { batch: ImportBatch }) {
  const t = useI18n().t;
  return <Badge tone={STATUS_TONE[batch.status]}>{t.imports.statuses[batch.status]}</Badge>;
}

function MappingStep({
  upload,
  onDone,
}: {
  upload: ImportUpload;
  onDone: (batchId: string) => void;
}) {
  const t = useI18n().t;
  const [mapping, setMapping] = useState<Record<string, string>>(upload.suggestedMapping);
  const [save, setSave] = useState(true);
  const [result, setResult] = useState<ImportMappingResponse | null>(null);
  const submit = useApiMutation<
    { mapping: Record<string, string>; save: boolean },
    ImportMappingResponse
  >('PUT', `/v1/imports/${upload.batch.id}/mapping`, { onSuccess: setResult });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t.imports.mapping}</CardTitle>
          <span className="text-sm text-muted-foreground">{upload.batch.filename}</span>
        </CardHeader>
        <Table>
          <THead>
            <tr>
              <TH>{t.imports.field}</TH>
              <TH>{t.imports.column}</TH>
            </tr>
          </THead>
          <TBody>
            {upload.fields.map((field) => (
              <TR key={field.key}>
                <TD>
                  {field.label}
                  {field.required ? <span className="text-destructive"> *</span> : null}
                </TD>
                <TD>
                  <Select
                    aria-label={field.label}
                    value={mapping[field.key] ?? ''}
                    onChange={(e) =>
                      setMapping((m) => {
                        const next = { ...m };
                        if (e.target.value) next[field.key] = e.target.value;
                        else delete next[field.key];
                        return next;
                      })
                    }
                  >
                    <option value="">{t.imports.ignore}</option>
                    {upload.headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </Select>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        <CardContent className="flex flex-wrap items-center gap-4 border-t border-border">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={save} onChange={(e) => setSave(e.target.checked)} />
            {t.imports.saveMapping}
          </label>
          <Button disabled={submit.isPending} onClick={() => submit.mutate({ mapping, save })}>
            {t.imports.validate}
          </Button>
          <ActionError error={submit.error} />
        </CardContent>
      </Card>
      {result ? (
        <Card>
          <CardHeader>
            <CardTitle>{t.imports.validate}</CardTitle>
            <Button disabled={result.missing.length > 0} onClick={() => onDone(result.batch.id)}>
              {t.imports.dryRun}
            </Button>
          </CardHeader>
          {result.missing.length ? (
            <p role="alert" className="px-4 pt-3 text-sm text-destructive">
              {t.imports.missing} : {result.missing.join(', ')}
            </p>
          ) : null}
          <Table>
            <THead>
              <tr>
                <TH>{t.imports.line}</TH>
                <TH>{t.imports.errors}</TH>
                <TH>{t.imports.warnings}</TH>
              </tr>
            </THead>
            <TBody>
              {result.preview.map((row) => (
                <TR key={row.line}>
                  <TD>{row.line}</TD>
                  <TD className="text-destructive">
                    {row.errors.join(' · ') || <span className="text-success">{t.imports.ok}</span>}
                  </TD>
                  <TD className="text-warning">{row.warnings.join(' · ')}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
}

function UploadForm({ onUploaded }: { onUploaded: (upload: ImportUpload) => void }) {
  const t = useI18n().t;
  const [kind, setKind] = useState<ImportKind>('contracts');
  const [file, setFile] = useState<File | null>(null);
  const upload = useApiMutation<FormData, ImportUpload>('POST', '/v1/imports', {
    invalidate: ['/v1/imports'],
    onSuccess: onUploaded,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!file) return;
    const form = new FormData();
    form.set('kind', kind);
    form.set('file', file);
    upload.mutate(form);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.imports.newImport}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
          <Field label={t.imports.kind}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as ImportKind)}>
              {IMPORT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t.imports.kinds[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t.imports.file}>
            <Input
              type="file"
              accept=".csv,.xlsx,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Field>
          <Button type="submit" disabled={!file || upload.isPending}>
            {t.imports.upload}
          </Button>
        </form>
        <div className="mt-2">
          <ActionError error={upload.error} />
        </div>
      </CardContent>
    </Card>
  );
}

export function ImportsPage() {
  const { t, language } = useI18n();
  const auth = useAuth();
  const navigate = useNavigate();
  const tz = useTenant().data?.timezone;
  const [upload, setUpload] = useState<ImportUpload | null>(null);
  const list = useCursorList<ImportBatch>('/v1/imports', {}, 20);

  if (!auth.can('editPortfolio')) return <Forbidden />;
  return (
    <>
      <PageHeader title={t.imports.title} subtitle={t.imports.exportsSoon} />
      <div className="flex flex-col gap-4">
        {upload ? (
          <MappingStep
            upload={upload}
            onDone={(id) => void navigate({ to: '/imports/$id', params: { id } })}
          />
        ) : (
          <UploadForm onUploaded={setUpload} />
        )}
        <Card>
          <CardHeader>
            <CardTitle>{t.imports.history}</CardTitle>
          </CardHeader>
          {list.error ? (
            <ErrorState error={list.error} />
          ) : list.isPending ? (
            <Loading />
          ) : list.items.length === 0 ? (
            <Empty />
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>{t.imports.file}</TH>
                  <TH>{t.imports.kind}</TH>
                  <TH>{t.installments.status}</TH>
                  <TH>{t.imports.rows}</TH>
                  <TH>{t.imports.createdAt}</TH>
                </tr>
              </THead>
              <TBody>
                {list.items.map((b) => (
                  <TR key={b.id}>
                    <TD>
                      <Link
                        to="/imports/$id"
                        params={{ id: b.id }}
                        className="font-medium text-primary hover:underline"
                      >
                        {b.filename}
                      </Link>
                    </TD>
                    <TD>{t.imports.kinds[b.kind]}</TD>
                    <TD className="flex flex-wrap gap-1">
                      <ImportStatusBadge batch={b} />
                      <Badge>{b.dryRun ? t.imports.simulation : t.imports.real}</Badge>
                    </TD>
                    <TD className="tabular-nums">
                      {b.rowsOk} / {b.rowsTotal}
                      {b.rowsError ? (
                        <span className="text-destructive">
                          {' '}
                          ({b.rowsError} {t.imports.rowsError})
                        </span>
                      ) : null}
                    </TD>
                    <TD>{instant(b.createdAt, tz, language)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          {list.hasNextPage ? (
            <div className="border-t border-border p-3 text-center">
              <Button variant="outline" onClick={() => void list.fetchNextPage()}>
                {t.common.loadMore}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>
    </>
  );
}
